import { TrackedMap } from 'tracked-built-ins';
import {
  adapterHelper,
  sortCohortByEnrichment,
} from 'client-portal/utils/clinicianSearch/send-all-catalog';
import { tracked } from '@glimmer/tracking';
import ENV from 'client-portal/config/environment';
import Service, { service } from '@ember/service';

const STATUS_PENDING = 'pending';
const STATUS_READY = 'ready';
const HARD_CAP = 100;
const BATCH_SIZE = 25;
const DEFAULT_ENRICHED_PAGES_TARGET = 2;

/**
 * Companion to the send-all clinician catalog: after filter changes the route
 * hands us the filtered cohort (and chip filters), we POST IDs in adaptive
 * batches to /client-portal-api/clinician-search/next-slots, then push
 * `next_available_at` into Ember Data so cards re-render and the Select
 * button enables.
 *
 * - `enrichCohort` batches of 25, stops early when the first two pages of
 *   sorted output are all has-slot, hard cap at 100.
 * - Caches per (clinicianId, officeIds, cptCodeRateId, weekdays,
 *   timeBuckets) for the session.
 * - Tracks per-clinician status (`pending` | `ready`) so cards can show a
 *   skeleton until the response arrives.
 */
export default class ClinicianSearchNextSlotsService extends Service {
  @service store;

  @tracked enrichmentVersion = 0;

  statuses = new TrackedMap();

  #cache = new Map();
  #errorKeys = new Set();
  #currentGeneration = 0;
  #onBatchComplete = null;

  setOnBatchComplete(callback) {
    this.#onBatchComplete = callback;
  }

  resetState() {
    this.#currentGeneration += 1;
    this.#cache.clear();
    this.#errorKeys.clear();
    this.statuses.clear();
    this.enrichmentVersion = 0;
  }

  statusFor(clinicianId) {
    return this.statuses.get(String(clinicianId));
  }

  enrichmentStatusFor(clinicianId, filters = {}) {
    let id = String(clinicianId);
    let normalized = this.#normalizeFilterArgs(filters);
    let chipKey = this.#cacheKeyFor(id, normalized);

    if (this.#errorKeys.has(chipKey)) {
      return 'error';
    }
    if (this.#cache.has(chipKey)) {
      return this.#cache.get(chipKey) ? 'hasSlot' : 'noSlot';
    }
    return 'unknown';
  }

  nextAvailableAt(clinicianId, filters = {}) {
    let id = String(clinicianId);
    let normalized = this.#normalizeFilterArgs(filters);
    let chipKey = this.#cacheKeyFor(id, normalized);

    if (!this.#cache.has(chipKey)) {
      return null;
    }
    return this.#cache.get(chipKey);
  }

  /**
   * @param {Array} records — visible page (legacy; delegates to enrichCohort)
   * @param {{ officeIds?: string[], cptCodeRateId?: string|number, weekdays?: number[], timeBuckets?: number[] }} filters
   */
  enrichVisible(records, filters = {}) {
    return this.enrichCohort(records, filters);
  }

  /**
   * Adaptive enrichment loop for a filtered cohort.
   *
   * @param {Array} cohort — full filtered cohort from send-all
   * @param {{ officeIds?: string[], cptCodeRateId?: string|number, weekdays?: number[], timeBuckets?: number[] }} filters
   * @param {{ pageNumber?: number }} [options] — the page the user is viewing.
   *   Drives how many sorted "visible" pages must be filled with hasSlot
   *   before the loop can early-stop, so deep-linked page 5 isn't stuck on
   *   batch-1's first 20 cards.
   */
  enrichCohort(cohort, filters = {}, options = {}) {
    if (!Array.isArray(cohort) || cohort.length === 0) {
      return;
    }

    let gen = ++this.#currentGeneration;
    void this.#runEnrichLoop(cohort, filters, gen, options);
  }

  async #runEnrichLoop(cohort, filters, gen, options = {}) {
    let normalized = this.#normalizeFilterArgs(filters);
    let pageSize = ENV.clinicianSearchPageSize ?? 10;
    let cursor = 0;

    let parsedPageNumber = parseInt(String(options.pageNumber ?? 1), 10);
    if (!Number.isFinite(parsedPageNumber) || parsedPageNumber < 1) {
      parsedPageNumber = 1;
    }
    let pagesTarget = Math.max(DEFAULT_ENRICHED_PAGES_TARGET, parsedPageNumber + 1);
    let visibleCardsTarget = pagesTarget * pageSize;

    // Bump the hard cap so deep pages still get enriched. With a 25-batch
    // size and an 8-thread executor on the SP side, this stays well under
    // the wall-clock budget for the common case.
    let dynamicHardCap = Math.max(HARD_CAP, parsedPageNumber * pageSize + BATCH_SIZE);
    let limit = Math.min(cohort.length, dynamicHardCap);

    while (cursor < limit) {
      if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
        return;
      }

      let batch = cohort.slice(cursor, cursor + BATCH_SIZE);
      let batchIds = batch
        .map(record => String(record?.id ?? ''))
        .filter(id => id && !this.#hasCached(id, normalized));

      if (batchIds.length) {
        try {
          await this.#flush({
            ids: batchIds,
            officeIds: normalized.officeIds,
            cptCodeRateId: normalized.cptCodeRateId,
            weekdays: normalized.weekdays,
            timeBuckets: normalized.timeBuckets,
          });
        } catch {
          this.#markBatchErrored(batchIds, normalized);
        }
      }

      cursor += BATCH_SIZE;

      if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
        return;
      }

      this.enrichmentVersion += 1;

      let sorted = sortCohortByEnrichment(
        cohort,
        id => this.enrichmentStatusFor(id, filters),
        id => this.nextAvailableAt(id, filters)
      );
      let visibleWindow = sorted.slice(0, visibleCardsTarget);
      if (
        visibleWindow.length > 0 &&
        visibleWindow.every(record => {
          let status = this.enrichmentStatusFor(record.id, filters);
          return status === 'hasSlot';
        })
      ) {
        break;
      }
    }
  }

  #isDestroyedOrDestroying() {
    return this.isDestroyed || this.isDestroying;
  }

  #markBatchErrored(ids, normalized) {
    for (let id of ids) {
      let cacheKey = this.#cacheKeyFor(id, normalized);
      this.#errorKeys.add(cacheKey);
      this.#cache.set(cacheKey, null);
      this.statuses.set(id, STATUS_READY);
    }
  }

  #hasCached(id, normalized) {
    return this.#cache.has(this.#cacheKeyFor(id, normalized));
  }

  #cacheKeyFor(id, { officeIds, cptCodeRateId, weekdays, timeBuckets }) {
    let officeKey = officeIds.join(',');
    let cptKey = cptCodeRateId ?? '';
    let weekdaysKey = weekdays.join(',');
    let bucketsKey = timeBuckets.join(',');
    return `${id}:${officeKey}:${cptKey}:${weekdaysKey}:${bucketsKey}`;
  }

  #normalizeFilterArgs(filters = {}) {
    return {
      officeIds: this.#normalizeIntList(filters.officeIds),
      cptCodeRateId: this.#normalizeId(filters.cptCodeRateId),
      weekdays: this.#normalizeIntList(filters.weekdays),
      timeBuckets: this.#normalizeIntList(filters.timeBuckets),
    };
  }

  async #flush({ ids, officeIds, cptCodeRateId, weekdays, timeBuckets }) {
    let normalized = { officeIds, cptCodeRateId, weekdays, timeBuckets };
    let toFetch = [];

    for (let id of ids) {
      let cacheKey = this.#cacheKeyFor(id, normalized);
      if (this.#cache.has(cacheKey)) {
        this.#applyToStore(id, this.#cache.get(cacheKey));
      } else {
        toFetch.push(id);
        this.statuses.set(id, STATUS_PENDING);
      }
    }

    if (toFetch.length === 0) {
      this.#onBatchComplete?.(ids);
      return;
    }

    /* eslint-disable camelcase -- next-slots wire format matches the Rails API */
    let body = {
      clinician_ids: toFetch.map(id => parseInt(id, 10)).filter(Number.isFinite),
    };
    if (officeIds.length) {
      body.office_ids = officeIds;
    }
    if (cptCodeRateId) {
      body.cpt_code_rate_id = parseInt(cptCodeRateId, 10);
    }
    if (weekdays.length) {
      body.weekdays = weekdays;
    }
    if (timeBuckets.length) {
      body.time_buckets = timeBuckets;
    }
    /* eslint-enable camelcase */

    let adapter = this.store.adapterFor('clinician');
    let url = adapterHelper(adapter, { nextSlots: true });
    let response = await adapter.ajax(url, 'POST', { data: body });
    let rows = response?.data ?? [];

    for (let row of rows) {
      let id = String(row.clinician_id);
      let nextAvailableAt = row.next_available_at ?? null;
      this.#cache.set(this.#cacheKeyFor(id, normalized), nextAvailableAt);
      this.#applyToStore(id, nextAvailableAt);
    }

    let returnedIds = new Set(rows.map(row => String(row.clinician_id)));
    for (let id of toFetch) {
      if (!returnedIds.has(id)) {
        this.#cache.set(this.#cacheKeyFor(id, normalized), null);
        this.#applyToStore(id, null);
      }
    }

    this.#onBatchComplete?.(ids);
  }

  #applyToStore(id, nextAvailableAt) {
    let stringId = String(id);
    // Always push — Ember Data merges the partial payload into the existing
    // clinician record (preserving catalog attrs set by send-all) or
    // creates a stub record if enrichment lands before pushPageToStore.
    // The previous `if (existing)` guard dropped the value on the floor
    // when the network response arrived before the catalog seed finished
    // running, leaving the card stuck on "No upcoming availability."
    this.store.push({
      data: {
        id: stringId,
        type: 'clinician',
        attributes: { nextAvailability: nextAvailableAt },
      },
    });
    this.statuses.set(stringId, STATUS_READY);
  }

  #normalizeId(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    let str = String(value).trim();
    return str.length ? str : null;
  }

  #normalizeIntList(values) {
    if (!Array.isArray(values)) {
      return [];
    }
    return [
      ...new Set(
        values
          .map(value => (typeof value === 'number' ? value : parseInt(String(value).trim(), 10)))
          .filter(Number.isFinite)
      ),
    ].sort((a, b) => a - b);
  }
}
